/**
 * @lodex-pro/web-admin/features/ops/alerts-labels — plain words for the alert tabs (#56 PR 2c).
 */
import type { IncidentNotification, IncidentStatus, QuietHours } from './alerts-hooks';
import type { Tone } from './labels';

const PARAM_LABELS: Readonly<Record<string, string>> = {
  windowMinutes: 'Look back (minutes)',
  minErrors: 'Errors, at least',
  baselineMultiple: 'Times the usual rate, at least',
  criticalErrors: 'Critical at (errors)',
  warningAt: 'Warn at',
  criticalAt: 'Critical at',
  graceMinutes: 'Stale after the sync interval plus (minutes)',
  sendFailuresAt: 'Warn at failed sends',
  complaintsAt: 'Warn at spam complaints',
  criticalSendFailuresAt: 'Critical at failed sends',
  oldestMinutes: 'Oldest waiting item (minutes)',
  criticalMultiple: 'Critical at this many times the limit',
  catalogReviewMax: 'Catalog changes waiting, at most',
  staleHours: 'Overdue after (hours)',
  bounceRateCriticalPercent: 'Critical at SES bounce rate (%)',
  minRequests: 'Requests in the window, at least',
  warningPercent: 'Warn at (% of requests failing)',
  criticalPercent: 'Critical at (% of requests failing)',
};

const PART_LABELS: Readonly<Record<string, string>> = {
  push_queued: 'Push notifications',
  operator_notification_jobs: 'Operator emails to prepare',
  operator_notification_deliveries: 'Operator emails to send',
  import_batches_running: 'Imports in progress',
  disposal_report_runs: 'Disposal reports',
  catalog_sync: 'State well catalog sync',
  dashboard_rollup: 'Dashboard history rollup',
  usage_snapshot: 'Nightly usage snapshot',
  invoice_run: 'Monthly invoice run',
};

export function paramLabel(key: string): string {
  return PARAM_LABELS[key] ?? key;
}

export function partLabel(key: string): string {
  return PART_LABELS[key] ?? key.replace(/_/g, ' ');
}

export const INCIDENT_STATUS_LABEL: Readonly<Record<IncidentStatus, string>> = {
  OPEN: 'Open',
  ACKED: 'Acknowledged',
  RESOLVED: 'Resolved',
};

export function incidentTone(s: IncidentStatus): Tone {
  return s === 'OPEN' ? 'danger' : s === 'ACKED' ? 'warning' : 'muted';
}

export const RESOLVE_REASON_LABEL: Readonly<Record<string, string>> = {
  AUTO_CLEARED: 'cleared on its own',
  MANUAL: 'resolved by hand',
  EXPIRED: 'expired',
};

export const NOTIFICATION_KIND_LABEL: Readonly<Record<IncidentNotification['kind'], string>> = {
  OPENED: 'New problem',
  REMINDER: 'Reminder',
  ESCALATED: 'Got worse',
  RESOLVED: 'Cleared',
  TEST: 'Test',
};

export const NOTIFICATION_STATUS_LABEL: Readonly<Record<IncidentNotification['status'], string>> = {
  SENT: 'Sent',
  FAILED: 'Failed',
  SUPPRESSED: 'Held (quiet hours)',
};

export function quietLabel(q: QuietHours | null): string {
  return q === null ? 'None' : `${q.start}–${q.end} ${q.timeZone}`;
}

/** What a recipient's minimum severity means, as a phrase: "You receive <this> at …". */
export function severityPhrase(min: 'WARNING' | 'CRITICAL'): string {
  return min === 'CRITICAL' ? 'critical alerts' : 'warnings and critical alerts';
}

/**
 * The dev fixture platform admins (docs/DEV_FIXTURE_ACCOUNTS.md:
 * fixture.s<N>.platformadmin@…). They are real accounts that proofs sign in as, so
 * they stay pickable, but labelled, so nobody adds a test account thinking it is a
 * person.
 */
export function isFixtureAccount(email: string): boolean {
  return /^fixture\.s\d+\./i.test(email);
}

/** The zones offered for quiet hours. */
export const QUIET_ZONES = [
  'America/Chicago',
  'America/Denver',
  'America/New_York',
  'America/Los_Angeles',
  'America/Phoenix',
  'UTC',
] as const;
